import { TileSize } from "game-common/models"

/**
 * WATER, BAKED: everything about a body of water that does not move, worked out once per room on the CPU
 * from the room's data-only `water` layer (common/room/ember/forest_pond.ts) -- the shader in
 * water_layer.ts draws the moving part from what this bakes.
 *
 * There is no autotile table. The shore is the CELL MASK, blurred and cut a little below one half, with a
 * world-keyed wobble: a straight run sits on its cell line, a staircase averages out into a curve, and a
 * lone cell still comes out as a round pool, so no shape the cells can make needs art of its own. The
 * drawn edge stays within a few px of the collision square.
 *
 * Every per-pixel variation is keyed on WORLD position, so two bakes of the same water always agree.
 */

/** How the cells read: 0 dry, otherwise water (1 shallow, 2 deep, 3 deeper -- WaterCell). Off the map is dry. */
export type WaterCells = (cx: number, cy: number) => number

/** A rectangle of the world, in px. */
export interface WaterRegion { x0: number; y0: number; w: number; h: number }

export const WATER_SHORE = { sigma: 13, cut: 0.42, wobble: 3.5 }
/** Height of the bank's earth face, in px: how far the land stands above the water. */
export const WATER_FACE_HEIGHT = 7

const hash = (x: number, y: number, seed: number): number => {
    let h = Math.imul(x | 0, 374761393) ^ Math.imul(y | 0, 668265263) ^ seed
    h = Math.imul(h ^ h >>> 13, 1274126177)
    return ((h ^ h >>> 16) >>> 0) / 4294967295
}
const smooth = (v: number) => v * v * (3 - 2 * v)
const valueNoise = (x: number, y: number, seed: number): number => {
    const ix = Math.floor(x), iy = Math.floor(y)
    const sx = smooth(x - ix), sy = smooth(y - iy)
    const a = hash(ix, iy, seed), b = hash(ix + 1, iy, seed), c = hash(ix, iy + 1, seed), d = hash(ix + 1, iy + 1, seed)
    const top = a + (b - a) * sx
    return top + ((c + (d - c) * sx) - top) * sy
}

/** One box pass along x then y, edges held. Three of these are close to a gaussian. */
const boxBlur = (src: Float32Array, w: number, h: number, r: number): Float32Array => {
    const tmp = new Float32Array(w * h), out = new Float32Array(w * h), span = 2 * r + 1
    for (let y = 0; y < h; y++) {
        const row = y * w
        let sum = 0
        for (let k = -r; k <= r; k++) sum += src[row + Math.min(w - 1, Math.max(0, k))]
        for (let x = 0; x < w; x++) {
            tmp[row + x] = sum / span
            sum += src[row + Math.min(w - 1, x + r + 1)] - src[row + Math.max(0, x - r)]
        }
    }
    for (let x = 0; x < w; x++) {
        let sum = 0
        for (let k = -r; k <= r; k++) sum += tmp[Math.min(h - 1, Math.max(0, k)) * w + x]
        for (let y = 0; y < h; y++) {
            out[y * w + x] = sum / span
            sum += tmp[Math.min(h - 1, y + r + 1) * w + x] - tmp[Math.max(0, y - r) * w + x]
        }
    }
    return out
}

/**
 * The cells that pass `test`, as 1 and 0 per world pixel of `region`, blurred to a gaussian of `sigma`. It
 * reads that far past the region, so two regions agree where they meet.
 */
const blurredCells = (cells: WaterCells, region: WaterRegion, sigma: number, test: (cell: number) => boolean): Float32Array => {
    // Three boxes of this radius are a gaussian of `sigma`.
    const r = Math.max(1, Math.round((Math.sqrt(4 * sigma * sigma + 1) - 1) / 2))
    const m = 3 * r + 1
    const W = region.w + 2 * m, H = region.h + 2 * m
    let field = new Float32Array(W * H), any = false
    for (let y = 0; y < H; y++) {
        const cy = Math.floor((region.y0 - m + y) / TileSize)
        for (let x = 0; x < W; x++) {
            if (test(cells(Math.floor((region.x0 - m + x) / TileSize), cy))) { field[y * W + x] = 1; any = true }
        }
    }
    const out = new Float32Array(region.w * region.h)
    // Nothing passed: nothing to blur (water with no deeper core skips the whole pass).
    if (!any) return out
    for (let pass = 0; pass < 3; pass++) field = boxBlur(field, W, H, r)
    for (let y = 0; y < region.h; y++) out.set(field.subarray((y + m) * W + m, (y + m) * W + m + region.w), y * region.w)
    return out
}

/** 1 per world pixel of `region` that is water. */
export const waterMask = (cells: WaterCells, region: WaterRegion, shore = WATER_SHORE): Uint8Array => {
    const field = blurredCells(cells, region, shore.sigma, cell => cell > 0)
    const mask = new Uint8Array(region.w * region.h)
    const k = shore.wobble / 40
    for (let y = 0; y < region.h; y++) for (let x = 0; x < region.w; x++) {
        const v = field[y * region.w + x]
        if (v <= 0.02) continue
        const px = region.x0 + x, py = region.y0 + y
        const wob = k * (2 * (valueNoise(px / 26, py / 26, 71) - 0.5) + 0.6 * (valueNoise(px / 8, py / 8, 72) - 0.5))
        if (v - shore.cut + wob > 0) mask[y * region.w + x] = 1
    }
    return mask
}

/** Exact euclidean distance (Felzenszwalb) from every pixel to the nearest pixel where mask === target. */
export const distanceTo = (mask: Uint8Array, w: number, h: number, target: 0 | 1): Float32Array => {
    const INF = 1e12, n = Math.max(w, h)
    const grid = new Float64Array(w * h), out = new Float32Array(w * h)
    const f = new Float64Array(n), d = new Float64Array(n), v = new Int32Array(n), z = new Float64Array(n + 1)
    const pass = (len: number) => {
        let k = 0
        v[0] = 0; z[0] = -INF; z[1] = INF
        for (let q = 1; q < len; q++) {
            let s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k])
            while (s <= z[k]) { k--; s = ((f[q] + q * q) - (f[v[k]] + v[k] * v[k])) / (2 * q - 2 * v[k]) }
            k++; v[k] = q; z[k] = s; z[k + 1] = INF
        }
        k = 0
        for (let q = 0; q < len; q++) { while (z[k + 1] < q) k++; d[q] = (q - v[k]) * (q - v[k]) + f[v[k]] }
    }
    for (let x = 0; x < w; x++) {
        for (let y = 0; y < h; y++) f[y] = mask[y * w + x] === target ? 0 : INF
        pass(h)
        for (let y = 0; y < h; y++) grid[y * w + x] = d[y]
    }
    for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) f[x] = grid[y * w + x]
        pass(w)
        for (let x = 0; x < w; x++) out[y * w + x] = Math.sqrt(d[x])
    }
    return out
}

export interface WaterField {
    mask: Uint8Array
    /** Per water pixel, px to the nearest land: the shader's depth, and how deep a wader stands. */
    depth: Float32Array
    /** Per water pixel, 0..1: how far into the DEEPER water (the waist-deep core), eased over its edge. */
    deeper: Float32Array
    /** RGBA: r = depth*4, g = 255 where the bank shades the water (sun upper right), b = 255 where the
     * nearest shore lies NORTH (its face is in view) down to 0 where it lies south. */
    field: Uint8Array
    /** RGBA: rg = the current per pixel, 128 = still (a pond is still; a river would write here);
     * b = `deeper`, 0..255. */
    flow: Uint8Array
}

/** The sun's direction per px of bank height (sun.ts leans shadows to the lower left), a touch exaggerated. */
const SHADE = { x: 0.23 * 2, y: -0.13 * 2 }

/** The deeper water's edge: blurred like the shore, and eased from where its cells' blur reads this low to this high. */
const DEEPER_EDGE = { sigma: 13, from: 0.2, to: 0.7 }
const DEEPER_CELL = 3

export const bakeWaterField = (cells: WaterCells, region: WaterRegion, faceHeight = WATER_FACE_HEIGHT): WaterField => {
    const { w, h } = region
    const mask = waterMask(cells, region)
    const depth = distanceTo(mask, w, h, 0)
    const deeper = blurredCells(cells, region, DEEPER_EDGE.sigma, cell => cell >= DEEPER_CELL)
    const field = new Uint8Array(w * h * 4), flow = new Uint8Array(w * h * 4)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        field[i * 4 + 3] = 255
        flow[i * 4] = 128; flow[i * 4 + 1] = 128; flow[i * 4 + 3] = 255
        if (!mask[i]) { depth[i] = 0; deeper[i] = 0; continue }
        deeper[i] = smooth(Math.max(0, Math.min(1, (deeper[i] - DEEPER_EDGE.from) / (DEEPER_EDGE.to - DEEPER_EDGE.from))))
        flow[i * 4 + 2] = Math.round(deeper[i] * 255)
        field[i * 4] = Math.min(63, depth[i]) * 4
        let shaded = false
        for (let k = 0.5; k <= faceHeight && !shaded; k += 0.5) {
            const qx = Math.round(x + SHADE.x * k), qy = Math.round(y + SHADE.y * k)
            if (qx >= 0 && qy >= 0 && qx < w && qy < h && !mask[qy * w + qx]) shaded = true
        }
        field[i * 4 + 1] = shaded ? 255 : 0
        const dn = depth[Math.min(h - 1, y + 2) * w + x] - depth[Math.max(0, y - 2) * w + x]
        const de = depth[y * w + Math.min(w - 1, x + 2)] - depth[y * w + Math.max(0, x - 2)]
        const g = Math.hypot(dn, de)
        field[i * 4 + 2] = Math.round(255 * (g > 0.01 ? 0.5 + 0.5 * dn / g : 0.5))
    }
    return { mask, depth, deeper, field, flow }
}

const SOIL = [92, 64, 38]
const DEEP = [26, 64, 31]

/**
 * The bank, drawn the way ground_rise draws a step -- the land is the raised side, the water the low one:
 * an earth face under the cut grass where the land drops toward the camera (wettest at the waterline), the
 * grass hanging over its top and a lit lip above that; the far shore's rim lit, its blades poking up over the
 * water; blades spilling over the sides. `ground` is the region's RGBA. Returns RGBA, opaque only where
 * the bank paints.
 */
export const paintWaterBank = (ground: Uint8ClampedArray, mask: Uint8Array, region: WaterRegion,
    faceHeight = WATER_FACE_HEIGHT): Uint8ClampedArray => {
    const { w, h, x0, y0 } = region
    const FH = Math.max(1, Math.round(faceHeight))
    const n = w * h, out = new Float32Array(n * 3), painted = new Uint8Array(n)
    for (let i = 0; i < n; i++) { out[i * 3] = ground[i * 4]; out[i * 3 + 1] = ground[i * 4 + 1]; out[i * 3 + 2] = ground[i * 4 + 2] }
    const wet = (x: number, y: number) => x >= 0 && y >= 0 && x < w && y < h && mask[y * w + x] === 1
    const g = (x: number, y: number, c: number) => ground[(Math.min(h - 1, Math.max(0, y)) * w + x) * 4 + c]
    const scale = (i: number, m: number) => { out[i * 3] *= m; out[i * 3 + 1] *= m; out[i * 3 + 2] *= m; painted[i] = 1 }
    const colHash = (x: number, k: number, seed: number) => hash(x0 + x, k, seed)
    // s: rows down to the first water pixel (1 = right above it); FH + 2 when there is none in reach.
    const s = new Uint8Array(n)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        if (mask[y * w + x]) continue
        let k = 1
        while (k <= FH + 1 && y + k < h && !mask[(y + k) * w + x]) k++
        s[y * w + x] = k <= FH + 1 && y + k < h ? k : FH + 2
    }
    const isFace = (i: number) => s[i] > 0 && s[i] <= FH
    const hangCap = Math.round(FH * 0.45)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!isFace(i)) continue
        const depth = FH - s[i] + 1, t = depth / FH, lip = y - depth
        const openSide = wet(x - 1, y) || wet(x + 1, y)
        const e = Math.min(1, Math.max(0, t - 0.45))
        const strand = depth <= Math.floor(colHash(x, 0, 41) * 6) && colHash(x, 1, 42) > 0.4
        const hang = Math.min(Math.floor(colHash(x, 2, 43) * 3.2), hangCap)
        for (let c = 0; c < 3; c++) {
            const top = g(x, lip, c)
            let v = openSide ? top * (0.80 - 0.12 * t) : (top * (0.62 - 0.24 * t) + DEEP[c] * 0.15) * (1 - 0.4 * e) + SOIL[c] * 0.42 * e
            if (strand) v = top * 0.78
            if (depth <= hang) v = top * (depth === hang ? 0.86 : 0.97)
            if (s[i] === 1) v = SOIL[c] * 0.42 + DEEP[c] * 0.12
            out[i * 3 + c] = v
        }
        painted[i] = 1
    }
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!mask[i] && s[i] === FH + 1 && colHash(x, 3, 44) > 0.25) scale(i, 1.13)
    }
    for (let y = 1; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!mask[i] && mask[i - w] && colHash(x, 9, 50) > 0.2) scale(i, 1.10)
    }
    const tip = new Uint8Array(n)
    for (let y = 0; y < h - 1; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (mask[i] && !mask[i + w] && colHash(x, 6, 47) > 0.45) {
            tip[i] = 1; painted[i] = 1
            for (let c = 0; c < 3; c++) out[i * 3 + c] = out[(i + w) * 3 + c] * 0.94
        }
    }
    for (let y = 0; y < h - 2; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (mask[i] && tip[i + w] && colHash(x, 7, 48) > 0.75) {
            painted[i] = 1
            for (let c = 0; c < 3; c++) out[i * 3 + c] = out[(i + 2 * w) * 3 + c] * 0.90
        }
    }
    for (const dx of [-1, 1]) {
        const edgeColour = new Float32Array(n * 3), edge = new Uint8Array(n)
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
            const i = y * w + x
            if (!mask[i] && wet(x + dx, y)) { edge[i] = 1; edgeColour.set(out.subarray(i * 3, i * 3 + 3), i * 3) }
        }
        for (let y = 0; y < h; y++) {
            const spill = Math.floor(hash((y0 + y) * 7 + (dx > 0 ? 1 : 0), 8, 49) * 2.6)
            for (let x = 0; x < w; x++) {
                const i = y * w + x
                if (!edge[i]) continue
                for (let k = 1; k <= Math.min(2, spill); k++) {
                    const tx = x + dx * k
                    if (!wet(tx, y)) continue
                    const j = y * w + tx
                    for (let c = 0; c < 3; c++) out[j * 3 + c] = edgeColour[i * 3 + c] * (0.92 - 0.06 * k)
                    painted[j] = 1
                }
                if (dx > 0) scale(i, 1.07)
            }
        }
    }
    const rgba = new Uint8ClampedArray(n * 4)
    for (let i = 0; i < n; i++) {
        if (!painted[i]) continue
        rgba[i * 4] = Math.min(255, Math.max(0, out[i * 3]))
        rgba[i * 4 + 1] = Math.min(255, Math.max(0, out[i * 3 + 1]))
        rgba[i * 4 + 2] = Math.min(255, Math.max(0, out[i * 3 + 2]))
        rgba[i * 4 + 3] = 255
    }
    return rgba
}

/**
 * The bank's reflection: each water pixel within a face's height of a north bank shows the land pixel the
 * same distance above the waterline -- the earth face and the grass hanging over it, upside down. RGBA,
 * opaque where there is something to reflect.
 */
export const bankReflection = (ground: Uint8ClampedArray, bank: Uint8ClampedArray, mask: Uint8Array, region: WaterRegion,
    faceHeight = WATER_FACE_HEIGHT): Uint8ClampedArray => {
    const { w, h } = region
    const out = new Uint8ClampedArray(w * h * 4)
    for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
        const i = y * w + x
        if (!mask[i]) continue
        let k0 = 0
        for (let k = 1; k <= faceHeight + 3; k++) {
            if (y - k < 0) break
            if (!mask[(y - k) * w + x]) { k0 = k; break }
        }
        if (!k0) continue
        const sy = y - 2 * k0 + 1
        if (sy < 0) continue
        const j = (sy * w + x) * 4
        const src = bank[j + 3] ? bank : ground
        out[i * 4] = src[j]; out[i * 4 + 1] = src[j + 1]; out[i * 4 + 2] = src[j + 2]
        out[i * 4 + 3] = k0 <= faceHeight ? 255 : 150
    }
    return out
}

/** The water cells' connected bodies (8-connected), each as its cell bounds. */
export const waterBodies = (cells: WaterCells, width: number, height: number): Array<{ x0: number; y0: number; x1: number; y1: number; cells: number }> => {
    const seen = new Uint8Array(width * height)
    const out: Array<{ x0: number; y0: number; x1: number; y1: number; cells: number }> = []
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
        if (seen[y * width + x] || !cells(x, y)) continue
        const body = { x0: x, y0: y, x1: x, y1: y, cells: 0 }
        const stack = [y * width + x]
        seen[y * width + x] = 1
        while (stack.length) {
            const i = stack.pop()!, cx = i % width, cy = Math.floor(i / width)
            body.cells++
            body.x0 = Math.min(body.x0, cx); body.x1 = Math.max(body.x1, cx)
            body.y0 = Math.min(body.y0, cy); body.y1 = Math.max(body.y1, cy)
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
                const nx = cx + dx, ny = cy + dy
                if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue
                const j = ny * width + nx
                if (!seen[j] && cells(nx, ny)) { seen[j] = 1; stack.push(j) }
            }
        }
        out.push(body)
    }
    return out
}
